import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import {
  LearningPath,
  LiveEvent,
  ModalityExtension,
  OfferingTypes,
} from 'types';
import CatalogStore from 'stores/Catalog';
import {
  PremiumSessionCollateral,
  PremiumSessionUserFacingStatus,
} from 'types/premiumSession';
import { CourseLvcUserFacingStatus } from 'views/Catalog/useCourseLiveClassInfo';
import useProgress from './hooks/useProgress';
import useSessionCollateralData from './hooks/useSessionCollateralData';
import { OfferingCardEntry } from './utils/types';
import { isCatalogEntry, isSkillsAssessmentEntry } from './utils/typeGuards';

type OfferingCardContextValue = {
  entry?: OfferingCardEntry;
  hasLiveVirtualClass?: boolean;
  hasProgress?: boolean;
  hasEntry?: boolean;
  isPremiumCard?: boolean;
  isLivePremiumSessionCourse?: boolean;
  isLocked?: boolean;
  isSearch?: boolean;
  learningPath?: LearningPath;
  liveEventInfo?: LiveEvent;
  lvcStatus?: CourseLvcUserFacingStatus;
  offeringType?: OfferingTypes;
  premiumSessionSlug?: string;
  premiumSessionStatus?: PremiumSessionUserFacingStatus;
  progress?: number;
  sessionCollateralData?: PremiumSessionCollateral;
};

const OfferingCardContext = createContext<OfferingCardContextValue | undefined>(
  undefined,
);

export const useOfferingCardContext = () => {
  const context = useContext(OfferingCardContext);

  if (!context) {
    throw new Error(
      'useOfferingCardContext must be used within OfferingCardContextProvider',
    );
  }

  return context;
};

type ProviderProps = Omit<
  OfferingCardContextValue,
  | 'hasEntry'
  | 'hasProgress'
  | 'isPremiumCard'
  | 'offeringType'
  | 'progress'
  | 'sessionCollateralData'
> & {
  children: ReactNode;
};

export const OfferingCardContextProvider = ({
  children,
  entry,
  hasLiveVirtualClass,
  isLivePremiumSessionCourse,
  isLocked,
  isSearch,
  learningPath,
  liveEventInfo,
  lvcStatus,
  premiumSessionSlug,
  premiumSessionStatus,
}: ProviderProps) => {
  const catalogEntry = isCatalogEntry(entry) ? entry : undefined;
  const { hasProgress, progress } = useProgress(catalogEntry?.code);
  const { sessionCollateralData } = useSessionCollateralData(
    premiumSessionSlug,
    Boolean(catalogEntry),
    isLivePremiumSessionCourse,
  );

  const memoizedContextValues = useMemo(() => {
    const hasEntry = Boolean(entry);
    let offeringType: OfferingTypes = entry?.modality;
    let isPremiumCard = false;

    if (isSkillsAssessmentEntry(entry)) {
      offeringType = ModalityExtension.SkillsAssessment;
    } else if (learningPath) {
      offeringType = ModalityExtension.SkillsPath;
    } else if (
      liveEventInfo &&
      (liveEventInfo.type === 'vt' || liveEventInfo.type === 'ilt')
    ) {
      offeringType = ModalityExtension.LiveEvents;
    } else if (isLivePremiumSessionCourse) {
      offeringType = ModalityExtension.LivePremiumSessionCourse;
      isPremiumCard = true;
    } else if (premiumSessionStatus) {
      offeringType = ModalityExtension.PremiumSession;
      isPremiumCard = true;
    } else if (hasLiveVirtualClass) {
      offeringType = ModalityExtension.LiveVirtualClass;
    } else if (catalogEntry) {
      offeringType =
        CatalogStore.resolveOfferingType(catalogEntry) ?? catalogEntry.modality;
    }

    return {
      entry,
      hasEntry,
      hasLiveVirtualClass,
      isLivePremiumSessionCourse,
      isLocked,
      isPremiumCard,
      liveEventInfo,
      learningPath,
      lvcStatus,
      offeringType,
      premiumSessionStatus,
    };
  }, [
    entry,
    hasLiveVirtualClass,
    isLivePremiumSessionCourse,
    isLocked,
    learningPath,
    liveEventInfo,
    lvcStatus,
    premiumSessionStatus,
  ]);

  return (
    <OfferingCardContext.Provider
      value={{
        ...memoizedContextValues,
        hasProgress,
        isSearch,
        premiumSessionSlug,
        progress,
        sessionCollateralData,
      }}
    >
      {children}
    </OfferingCardContext.Provider>
  );
};
